import type { ReactElement } from "react";

import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { BookOpen } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Session response from API
 */
interface SessionResponse {
  session_token?: string;
  user?: {
    id: string;
    email: string;
    name: string;
  };
}

/**
 * Google callback response from API
 */
interface GoogleCallbackResponse {
  session_token?: string;
  user?: {
    id: string;
    email: string;
    name: string;
  };
}

/**
 * AuthCallback Component
 *
 * Handles Google OAuth callback and session establishment.
 */
const AuthCallback = (): ReactElement => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef<boolean>(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processGoogleCallback = async (): Promise<void> => {
      try {
        // Google OAuth returns code in query params (not hash)
        const searchParams = new URLSearchParams(location.search);
        const code = searchParams.get("code");
        const error = searchParams.get("error");

        if (error) {
          console.error("Google OAuth error:", error);
          navigate("/login", { replace: true });
          return;
        }

        if (!code) {
          // Fallback: check for legacy Emergent auth (hash-based)
          const hash = location.hash;
          if (hash) {
            const hashParams = new URLSearchParams(hash.substring(1));
            const sessionId = hashParams.get("session_id");
            if (sessionId) {
              // Handle legacy Emergent auth
              const response = await axios.post<SessionResponse>(
                `${API}/auth/session`,
                {
                  session_id: sessionId,
                },
                {
                  withCredentials: true,
                }
              );
              navigate("/home", {
                state: { user: response.data },
                replace: true,
              });
              return;
            }
          }
          console.error("No authorization code in URL");
          navigate("/login", { replace: true });
          return;
        }

        // Exchange code for session with our backend
        const redirectUri = window.location.origin + "/auth/callback";
        const response = await axios.post<GoogleCallbackResponse>(
          `${API}/auth/google/callback`,
          {
            code: code,
            redirect_uri: redirectUri,
          },
          {
            withCredentials: true,
          }
        );

        // Store session token in localStorage for cross-origin requests
        if (response.data.session_token) {
          localStorage.setItem("session_token", response.data.session_token);
        }

        // Redirect to home with user data
        navigate("/home", { state: { user: response.data }, replace: true });
      } catch (error) {
        console.error("Auth callback error:", error);
        navigate("/login", { replace: true });
      }
    };

    processGoogleCallback();
  }, [navigate, location]);

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{
        background: "linear-gradient(to bottom, #FFF8F0 0%, #FFE8D6 100%)",
      }}
    >
      <div className="text-center">
        <BookOpen className="w-16 h-16 mx-auto mb-4 text-orange-400 animate-pulse" />
        <p
          className="text-lg"
          style={{ fontFamily: "Chewy, cursive", color: "#5C4033" }}
        >
          Logging you in...
        </p>
      </div>
    </div>
  );
};

export default AuthCallback;
